import {useCallback, useState} from 'react'
import {isFeatureEnabled} from '@github-ui/feature-flags'
import type {AgentMode} from '@github-ui/copilot-shared-ui/utils/agent-mode'
import type {TaskComposerProps} from '@github-ui/one-copilot-composer/shared'
import {useStoredMode} from '@github-ui/one-copilot-composer/new'
import {CLIModePicker} from '../components/task-view/CLIModePicker'

type ApprovalMode = NonNullable<TaskComposerProps['approvalMode']>

export type ComposerModeControls = Pick<TaskComposerProps, 'agentMode' | 'approvalMode' | 'modePicker'>

/**
 * Owns the agent-mode and tool-permission selection for a new-session composer and
 * renders the picker that exposes them. Every field is `undefined` while the
 * independent mode controls flag is off, so hosts keep the legacy persisted
 * permission selector.
 */
export function useComposerModeControls(options?: {portalContainerName?: string}): ComposerModeControls {
  const independentModeControlsEnabled = isFeatureEnabled('copilot_mission_control_independent_mode_controls')
  const [selectedMode, setSelectedMode] = useStoredMode()
  const [approvalMode, setApprovalMode] = useState<ApprovalMode>('assisted')
  const handleModeChange = useCallback((mode: AgentMode) => setSelectedMode(mode), [setSelectedMode])
  const portalContainerName = options?.portalContainerName
  const renderModePicker = useCallback<NonNullable<TaskComposerProps['modePicker']>>(
    ({availableModes, supportsApprovalMode}) => (
      <CLIModePicker
        selectedMode={selectedMode}
        onModeChange={handleModeChange}
        availableModes={availableModes}
        selectedApprovalMode={supportsApprovalMode ? approvalMode : undefined}
        onApprovalModeChange={supportsApprovalMode ? setApprovalMode : undefined}
        {...(portalContainerName ? {portalContainerName} : {})}
      />
    ),
    [approvalMode, handleModeChange, portalContainerName, selectedMode],
  )

  // Callers spread the result, so the keys stay present (and explicitly undefined)
  // to keep the composer's prop shape stable when the flag is off.
  if (!independentModeControlsEnabled) return {agentMode: undefined, approvalMode: undefined, modePicker: undefined}

  return {agentMode: selectedMode, approvalMode, modePicker: renderModePicker}
}
